import { ReactNode, useCallback, useId, useRef, useState } from 'react';

import styled from '@mint-lib/styled';
import Tooltip, { TooltipProps as TooltipPropsM } from '@mui/material/Tooltip';

import Typography, { TypographyProps } from '../Typography/Typography.jsx';

export interface TruncationProps extends TypographyProps {
  /**
   * Tooltip Title
   */
  tooltipTitle?: TooltipPropsM['title'];
  /**
   * Tooltip Placement
   */
  tooltipPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  /**
   * Children of the component
   */
  children?: ReactNode;
  /**
   * variant of Typography
   */
  variant?: TypographyProps['variant'];
  TooltipProps?: Partial<TooltipPropsM>;

  customMouseEnterHandler?: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => boolean;
}

/**
 *
 * @deprecated Use TruncateText from @mint-lib/tw-components instead
 */
const Truncation = ({
  tooltipTitle,
  TooltipProps,
  tooltipPlacement = 'top',
  children,
  variant = 'bodyShort01',
  customMouseEnterHandler,
  ...props
}: TruncationProps) => {
  const [isOverflowed, setIsOverflowed] = useState(false);

  const tid = useId();

  const mouseEnterHandler = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const element = e.currentTarget as HTMLElement;

      if (customMouseEnterHandler) {
        const hasOverflow = customMouseEnterHandler(e);
        setIsOverflowed(hasOverflow);
        return;
      }

      setIsOverflowed(element.scrollWidth > element.offsetWidth);
    },
    [customMouseEnterHandler],
  );

  return (
    <Tooltip
      title={tooltipTitle || ''}
      arrow
      placement={tooltipPlacement}
      disableHoverListener={!isOverflowed}
      id={tid}
      {...TooltipProps}
    >
      <TruncationM
        onMouseEnter={mouseEnterHandler}
        variant={variant}
        {...props}
      >
        {children}
      </TruncationM>
    </Tooltip>
  );
};

export default Truncation;

const TruncationM = styled(Typography)({
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflowWrap: 'break-word',
  display: 'block',
});
